import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SvgIconComponent } from 'angular-svg-icon';
import { catchError, Observable, of, take } from 'rxjs';
import { ButtonComponent } from '../../../../aptem-component-library/components/button/button.component';
import { TextInputComponent } from '../../../../aptem-component-library/components/text-input/text-input.component';
import { PageUtils } from '../../common/page.utils';
import { ErrorMessages } from '../../constants/error-messages';
import { AssessApiService } from '../../services/assess-api.service';
import { CampaignSettingsState } from '../../store/models/assessment-models';
import { CampaignContentState } from '../../store/models/campaign-models';
import {
  selectCampaignContentByKey,
  selectCampaignSettingsShowExternalIdField,
} from '../../store/selectors/campaign.selectors';
import { ContentComponent } from '../common/content/content.component';
import { FormPageComponent } from '../common/form/form.component';

@Component({
  selector: 'registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgIconComponent,
    ButtonComponent,
    FormPageComponent,
    TextInputComponent,
    ReactiveFormsModule,
    AsyncPipe,
    ContentComponent,
  ],
})
export class RegistrationComponent {
  public landingForm: FormGroup;
  protected isSubmitting: boolean = false;
  protected pageHeadingKey: string = 'registration-page-heading';
  protected pageHeadingValue$: Observable<string | null>;
  protected pageBodyKey: string = 'registration-page-body';
  protected pageBodyValue$: Observable<string | null>;
  protected externalIdLabelKey: string = 'register-externalid-label';
  protected externalIdLabelValue$: Observable<string | null>;
  protected externalIdValidationMessageKey: string = 'registration-externalId-validationMessage';
  protected externalIdValidationMessageValue$: Observable<string | null>;
  protected showExternalIdField$: Observable<boolean | null>;

  constructor(
    private apiService: AssessApiService,
    private pageUtils: PageUtils,
    private router: Router,
    private route: ActivatedRoute,
    private contentStore: Store<CampaignContentState>,
    private campaignSettingsStore: Store,
  ) {
    this.landingForm = new FormGroup({
      fullName: new FormControl('', [
        Validators.required,
        Validators.maxLength(500),
        Validators.pattern(/^[a-zA-Z0-9-' ]+$/),
      ]),
      emailAddress: new FormControl('', [Validators.required, Validators.maxLength(500), Validators.email]),
    });
    this.pageHeadingValue$ = this.contentStore.select(selectCampaignContentByKey(this.pageHeadingKey));
    this.pageBodyValue$ = this.contentStore.select(selectCampaignContentByKey(this.pageBodyKey));
    this.externalIdLabelValue$ = this.contentStore.select(selectCampaignContentByKey(this.externalIdLabelKey));
    this.externalIdValidationMessageValue$ = this.contentStore.select(
      selectCampaignContentByKey(this.externalIdValidationMessageKey),
    );
    this.showExternalIdField$ = this.campaignSettingsStore.select(selectCampaignSettingsShowExternalIdField);

    this.showExternalIdField$.subscribe((showExternalId) => {
      if (showExternalId) {
        this.landingForm.addControl(
          'externalId',
          new FormControl('', [Validators.required, Validators.maxLength(250), Validators.pattern(/^[a-zA-Z0-9-]+$/)]),
        );
      } else {
        this.landingForm.removeControl('externalId');
      }
    });
  }

  onSubmit(): void {
    if (this.landingForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.processSubmit();
    } else {
      return;
    }
  }

  processSubmit(): void {
    const routeParams = this.pageUtils.getLandingRoutePathParameters(this.route.snapshot);

    this.apiService
      .registerUser(
        routeParams.orgCode,
        routeParams.campaignCode,
        this.landingForm.value.fullName,
        this.landingForm.value.emailAddress,
        this.landingForm.value.externalId,
      )
      .pipe(
        take(1),
        catchError((err: HttpErrorResponse) => {
          console.log(err);

          this.pageUtils.navigateToErrorPage(ErrorMessages.registrationError);

          return of(null);
        }),
      )
      .subscribe((response: HttpResponse<any>) => {
        this.landingForm.reset();
        this.landingForm.disable();

        if (response !== null && response.status === 202) {
          const url = this.pageUtils.buildParameterisedContentRoute('registration-completed');
          this.router.navigateByUrl(url);
        }

        this.isSubmitting = false;
      });
  }
}
